import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@louise/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import type {
  CorrectFertilityGoalPayload,
  FertilityGoalCorrection,
  FertilityIntent,
  CreateFertilityIntentPayload,
  UpdateFertilityIntentPayload,
} from "../fertilityIntent.types";
import { experienceContextQueryKey } from "@src/packages/experience/hooks/useExperienceContext";

/**
 * Query key factory for fertility intent
 */
export const fertilityIntentKeys = {
  all: ["fertility-intent"] as const,
  detail: (tenantId: string, patientId: string) =>
    [...fertilityIntentKeys.all, tenantId, patientId] as const,
};

export const fertilityGoalCorrectionKeys = {
  all: ["fertility-goal-corrections"] as const,
  history: (patientId: string) =>
    [...fertilityGoalCorrectionKeys.all, patientId] as const,
};

function createIdempotencyKey() {
  const runtimeCrypto = globalThis.crypto;
  if (runtimeCrypto?.randomUUID) return runtimeCrypto.randomUUID();

  const randomHex = (length: number) =>
    Array.from({ length }, () =>
      Math.floor(Math.random() * 16).toString(16),
    ).join("");
  const variant = (8 + Math.floor(Math.random() * 4)).toString(16);
  return `${randomHex(8)}-${randomHex(4)}-4${randomHex(3)}-${variant}${randomHex(3)}-${randomHex(12)}`;
}

type UpdateFertilityIntentVariables = {
  payload: CreateFertilityIntentPayload | UpdateFertilityIntentPayload;
  mode: "create" | "update";
};

/**
 * Hook to get fertility intent data
 */
export function useFertilityIntent() {
  const { client, tenantId, patientId } = useApiClient();

  return useQuery<FertilityIntent | null>({
    queryKey: fertilityIntentKeys.detail(tenantId, patientId),
    queryFn: async () => {
      const { data, error, response } = await client.GET(
        "/{tenantId}/patients/{patientId}/fertility-intent",
        {
          params: {
            path: {
              tenantId,
              patientId,
            },
          },
        },
      );

      if (error) {
        // 404 means no fertility intent exists yet - return null silently
        if (response?.status === 404) {
          return null;
        }
        throwApiError(
          error,
          "Failed to load fertility intent",
          "useFertilityIntent",
          response?.status,
        );
      }

      return data?.data ?? null;
    },
    enabled: !!patientId && !!tenantId,
  });
}

/**
 * Hook to create or update fertility intent
 * Uses POST to create (when no data exists) or PATCH to update (when data exists)
 */
export function useUpdateFertilityIntent() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const queryKey = fertilityIntentKeys.detail(tenantId, patientId);

  return useMutation({
    mutationFn: async ({ payload, mode }: UpdateFertilityIntentVariables) => {
      if (mode === "update") {
        // PATCH to update existing
        const { data, error, response } = await client.PATCH(
          "/{tenantId}/patients/{patientId}/fertility-intent",
          {
            params: {
              path: {
                tenantId,
                patientId,
              },
            },
            body: payload as UpdateFertilityIntentPayload,
          },
        );

        if (error) {
          throwApiError(
            error,
            "Failed to update fertility intent",
            "useUpdateFertilityIntent",
            response?.status,
          );
        }

        return data?.data;
      } else {
        // POST to create new
        const { data, error, response } = await client.POST(
          "/{tenantId}/patients/{patientId}/fertility-intent",
          {
            params: {
              path: {
                tenantId,
                patientId,
              },
            },
            body: payload as CreateFertilityIntentPayload,
          },
        );

        if (error) {
          throwApiError(
            error,
            "Failed to create fertility intent",
            "useUpdateFertilityIntent",
            response?.status,
          );
        }

        return data?.data;
      }
    },
    onMutate: async ({ payload }: UpdateFertilityIntentVariables) => {
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<FertilityIntent | null>(
        queryKey,
      );

      // Optimistically update
      queryClient.setQueryData<FertilityIntent | null>(queryKey, (current) => {
        if (!current) {
          return payload as unknown as FertilityIntent;
        }
        return {
          ...current,
          ...payload,
        };
      });

      return { previous };
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

/** Read the append-only correction history for the current fertility intent. */
export function useFertilityGoalCorrectionHistory() {
  const { client, patientId } = useApiClient();
  const queryKey = fertilityGoalCorrectionKeys.history(patientId);

  return useQuery<FertilityGoalCorrection[]>({
    queryKey,
    queryFn: async () => {
      if (!patientId) return [];

      const { data, error, response } = await client.GET(
        "/patients/{patientId}/fertility-intent/corrections",
        { params: { path: { patientId } } },
      );

      if (error) {
        throwApiError(
          error,
          "Failed to load fertility goal corrections",
          "useFertilityGoalCorrectionHistory",
          response?.status,
        );
      }

      return data?.data ?? [];
    },
    enabled: !!patientId,
  });
}

type CorrectFertilityGoalVariables = {
  payload: CorrectFertilityGoalPayload;
  /** Reuse this key when retrying the same user action. */
  idempotencyKey?: string;
};

/**
 * Correct the goal through the versioned server command. The mutation updates
 * the local intent immediately from the authoritative response and refreshes
 * every projection that can depend on the changed intent.
 */
export function useCorrectFertilityGoal() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const intentQueryKey = fertilityIntentKeys.detail(tenantId, patientId);
  const correctionHistoryQueryKey =
    fertilityGoalCorrectionKeys.history(patientId);

  return useMutation({
    mutationFn: async ({
      payload,
      idempotencyKey = createIdempotencyKey(),
    }: CorrectFertilityGoalVariables) => {
      if (!patientId) throw new Error("No patient context");

      const { data, error, response } = await client.POST(
        "/patients/{patientId}/fertility-intent/corrections",
        {
          params: {
            path: { patientId },
            header: { "Idempotency-Key": idempotencyKey },
          },
          body: payload,
        },
      );

      if (error) {
        throwApiError(
          error,
          "Failed to correct fertility goal",
          "useCorrectFertilityGoal",
          response?.status,
        );
      }
      if (!data?.data) {
        throw new Error("Fertility goal correction response was empty");
      }

      return data.data;
    },
    onSuccess: ({ intent }) => {
      queryClient.setQueryData<FertilityIntent | null>(intentQueryKey, intent);
      void queryClient.invalidateQueries({
        queryKey: correctionHistoryQueryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: experienceContextQueryKey(patientId),
      });
      void queryClient.invalidateQueries({
        queryKey: ["patient", tenantId, patientId],
      });
    },
  });
}
